/** How the SEND report words and keys what it shows: counts, wire tokens, files and domain sets. */
import { sendDatasetOf } from './SendDomains';

/** `count` and the word for it, in the singular for exactly one. */
export const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

/** A wire token as words, e.g. `controlled_terminology` → `Controlled Terminology`. */
export const humanize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/** Categories whose token, read as words, would mislabel the finding. */
const FINDING_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  duplicate_dataset: 'Dataset uploaded more than once',
};

/** What a finding's category reads as in the report: its own label where it has one, else its words. */
export const findingCategoryLabel = (category: string): string =>
  FINDING_CATEGORY_LABELS[category] ?? humanize(category);

/**
 * A stable, unique key for each file of a result, in order. Usually its physical dataset (`DM`,
 * `SUPPMI`), but a bundle can hold two files of one dataset, so a repeat is numbered (`DM`, `DM-2`).
 */
export const fileKeysOf = (
  files: ReadonlyArray<{ domain_code: string; dataset_name?: string | null }>
): Array<string> => {
  const seen = new Map<string, number>();
  return files.map((file) => {
    const dataset = sendDatasetOf(file);
    const occurrence = (seen.get(dataset) ?? 0) + 1;
    seen.set(dataset, occurrence);
    return occurrence === 1 ? dataset : `${dataset}-${occurrence}`;
  });
};

/** The label for a set of domains a cross-domain check spanned, e.g. `DM ↔ LB` or `DM ↔ SE ↔ TA`. */
export const domainSetLabel = (domains: ReadonlyArray<string>): string => domains.join(' ↔ ');
